import * as React from 'react'
import { useMemo } from 'react'
import { Button, Divider, Menu, MenuItemRadio, MenuList, MenuPopover, MenuTrigger, mergeClasses, Tooltip } from '@fluentui/react-components'
import { CalendarDayRegular, CalendarRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import { BaseIntervalOverviewFilterType, generateFromDate, generateToDate } from './BaseOverviewFilter'
import { BaseOverviewProps, useOverviewStyles } from './BaseOverview'
import { EntityDto } from '../../config/appConfig'

export type BaseIntervalOverviewFilterProps = BaseOverviewProps<EntityDto, BaseIntervalOverviewFilterType> & {
    filterItems?: BaseIntervalOverviewFilterType[]
    reverse?: boolean
}

export default function BaseIntervalFilterMenu(props: BaseIntervalOverviewFilterProps) {
    const { values, setValues } = useFormikContext<BaseIntervalOverviewFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()

    const checkedValues = useMemo<Record<string, string[]>>(
        () => ({
            tag: values.tag ? [`${values.tag}`] : [],
        }),
        [values.tag],
    )

    const filterTagDayItems: BaseIntervalOverviewFilterType[] = useMemo(
        () =>
            [
                {
                    from: generateFromDate('monday', props.reverse),
                    to: generateToDate('monday', props.reverse),
                    tag: 'monday',
                    icon: <CalendarDayRegular />,
                },
                {
                    from: generateFromDate('tuesday', props.reverse),
                    to: generateToDate('tuesday', props.reverse),
                    tag: 'tuesday',
                    icon: <CalendarDayRegular />,
                },
                {
                    from: generateFromDate('wednesday', props.reverse),
                    to: generateToDate('wednesday', props.reverse),
                    tag: 'wednesday',
                    icon: <CalendarDayRegular />,
                },
                {
                    from: generateFromDate('thursday', props.reverse),
                    to: generateToDate('thursday', props.reverse),
                    tag: 'thursday',
                    icon: <CalendarDayRegular />,
                },
                {
                    from: generateFromDate('friday', props.reverse),
                    to: generateToDate('friday', props.reverse),
                    tag: 'friday',
                    icon: <CalendarDayRegular />,
                },
                {
                    from: generateFromDate('saturday', props.reverse),
                    to: generateToDate('saturday', props.reverse),
                    tag: 'saturday',
                    icon: <CalendarDayRegular />,
                },
                {
                    from: generateFromDate('sunday', props.reverse),
                    to: generateToDate('sunday', props.reverse),
                    tag: 'sunday',
                    icon: <CalendarDayRegular />,
                },
            ]
                .filter(Boolean)
                .sort((a, b) => a.from.localeCompare(b.from)),
        [props.reverse],
    )

    return (
        <Menu positioning={{ autoSize: true }} checkedValues={checkedValues}>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip withArrow relationship={'label'} content={translate('base.filter.interval.tooltip')}>
                    <Button
                        appearance={'secondary'}
                        className={mergeClasses(overviewStyles.filterTagButton, values.tag && overviewStyles.filterTagButtonSelected)}
                        icon={!values.tag ? <CalendarRegular /> : undefined}
                    >
                        {values.tag && translate(`base.filter.interval.${values.tag}`)}
                    </Button>
                </Tooltip>
            </MenuTrigger>

            <MenuPopover>
                <MenuList>
                    {filterTagDayItems?.map((tag, index) => (
                        <MenuItemRadio
                            key={index}
                            name={'tag'}
                            value={`${tag.tag}`}
                            onClick={() => {
                                setValues({
                                    ...values,
                                    ...tag,
                                })
                            }}
                        >
                            {translate(tag.title ? tag.title : `base.filter.interval.${tag.tag}`)}
                        </MenuItemRadio>
                    ))}
                    {filterTagDayItems?.length > 0 && <Divider />}
                    {props.filterItems?.map((tag, index) => (
                        <MenuItemRadio
                            key={index}
                            name={'tag'}
                            value={`${tag.tag}`}
                            onClick={() => {
                                setValues({
                                    ...values,
                                    ...tag,
                                })
                            }}
                        >
                            {translate(tag.title ? tag.title : `base.filter.interval.${tag.tag}`)}
                        </MenuItemRadio>
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}
